import { useBooleanState } from '@cian/react-utils';
import { Button, ModalWindow, Spinner } from '@cian/ui-kit';
import loadable from '@loadable/component';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NO_SSR } from 'shared/constants/loadable';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { useAgentSelector } from 'shared/hooks/selectors/useAgentSelector';
import { useOfferPreparedPhonesSelector } from 'shared/hooks/selectors/useOfferPreparedPhonesSelector';
import { useOfferSelector } from 'shared/hooks/selectors/useOfferSelector';
import { useDynamicPhoneFlow } from 'shared/hooks/useDynamicPhoneFlow';
import { useOfferDynamicCalltrackingEnabled } from 'shared/hooks/useOfferDynamicCalltrackingEnabled';
import { selectAgentName } from 'shared/selectors';
import { selectDynamicPhones } from 'shared/selectors/offerData/getActualPhones';
import { extractUndefinedValue } from 'shared/utils/extractUndefinedValue';
import { getFormattedPhoneNumber } from 'shared/utils/formatters';
import { getAgentAvatar } from 'shared/utils/getAgentAvatar';
import { trackShowStatNumber } from 'shared/utils/tracking/trackShowStatNumber';
import { v4 as uuid } from 'uuid';

import styles from './ElectronicTradingContactButtonContainer.css';
import { trackJoinTrade, trackOpenTradingModal } from './internal/tracking';

import type { TButtonSize } from '@cian/ui-kit/button/types';

const ElectronicTradingWidgetLoadable = loadable.lib(() => import('@cian/frontend-electronic-trading-widget'), {
  ssr: NO_SSR,
});

const MODAL_WIDTHS = {
  narrow: 498,
  wide: 680,
} as const;

type ElectronicTradingContactButtonContainerProps = {
  /**
   * @default 'M'
   */
  buttonSize?: TButtonSize;
  /**
   * @default false
   */
  buttonFullWidth?: boolean;
  dataTestId?: string;
};

export const ElectronicTradingContactButtonContainer: FC<ElectronicTradingContactButtonContainerProps> = props => {
  const { buttonSize = 'M', buttonFullWidth = false, dataTestId } = props;

  const { config } = useApplicationContext();

  const agent = useAgentSelector();
  const offer = useOfferSelector();

  const { tradingLink } = useOfferSelector();

  const staticPhones = useOfferPreparedPhonesSelector();
  const dynamicPhones = useSelector(selectDynamicPhones);
  const publisherName = useSelector(selectAgentName);

  const isOfferDynamicCalltrackingEnabled = useOfferDynamicCalltrackingEnabled();
  const { requestPhone } = useDynamicPhoneFlow();
  const [isDynamicPhoneLoading, setDynamicPhoneLoading] = useState(false);

  /** Подменные номера динКТ приоритетнее статических из оффера */
  const phones = useMemo(
    () => (dynamicPhones ? dynamicPhones.map(getFormattedPhoneNumber).filter(Boolean) : staticPhones),
    [dynamicPhones, staticPhones],
  );

  const modalState = useBooleanState();

  const onOpenTradingModal = useCallback(async () => {
    // ДинКТ вторички (SEL-998): номер в модалку — подменный; модалка открывается и без номера (там переход к торгам)
    const clickId = isOfferDynamicCalltrackingEnabled ? uuid() : undefined;

    trackOpenTradingModal(offer, agent, clickId);

    if (clickId) {
      setDynamicPhoneLoading(true);
      await requestPhone({
        offerId: offer.id,
        clickId,
        hasStaticPhones: staticPhones.length > 0,
        onDegradation: () => trackShowStatNumber({ offer, agent, user: null, clickId }),
      });
      setDynamicPhoneLoading(false);
    }

    modalState.setTrue();
  }, [offer, agent, modalState, isOfferDynamicCalltrackingEnabled, requestPhone, staticPhones]);

  if (!tradingLink) {
    return null;
  }

  const legalLink = config.getStrict<string>('electronicTrading.legalDocsUrl');

  return (
    <>
      <ModalWindow
        open={modalState.state}
        onClose={modalState.setFalse}
        portal
        size="M"
        fixed
        maxWidth={phones.length ? MODAL_WIDTHS.wide : MODAL_WIDTHS.narrow}
        title="Переходим к электронным торгам"
        content={
          <ElectronicTradingWidgetLoadable
            fallback={
              <div className={styles['modal-content-fallback']}>
                <Spinner size={40} />
              </div>
            }
          >
            {Lib => (
              <Lib.ElectronicTradingWidget
                legalLink={legalLink}
                trackLegalLinkClick={/* istanbul ignore next */ (): void => {}}
                joinTradeButtonLink={tradingLink}
                trackJoinTrade={() => trackJoinTrade(offer)}
                publisherPhoneNumbers={phones}
                publisherAvatarSrc={agent && getAgentAvatar(agent)}
                publisherTypeLabel={extractUndefinedValue(agent?.formattedPublisherType)}
                publisherName={publisherName}
                trackPublisherPhoneNumberClick={/* istanbul ignore next */ (): void => {}}
                deviceType="desktop"
              />
            )}
          </ElectronicTradingWidgetLoadable>
        }
      />
      <Button
        fullWidth={buttonFullWidth}
        size={buttonSize}
        onClick={onOpenTradingModal}
        data-testid={dataTestId}
        loading={isDynamicPhoneLoading}
      >
        Принять участие в торгах
      </Button>
    </>
  );
};
